// history-row.js — CLIENT-SIDE shaping for the queue HISTORY views (Queue.jsx
// "Yesterday" / "Earlier this week", DEFERRED Group C item 3). A completed visit
// is a settled record, so the history row carries more than the live queue row:
// token · patient · doctor · department · timing (the called→completed span).
//
// REUSES shapeQueueRow (the live-queue shaper) verbatim for the common fields —
// token, name, doctor, department, state, time, ids — then augments it with the
// visit's DURATION derived from Encounter.period (start = check-in/called instant,
// end = completed instant). No new FHIR read; the history loader already holds the
// Encounter + its Patient. Pure + dependency-light so it unit-tests without I/O.
import { shapeQueueRow } from './queue-row.js';

// Minutes between the encounter's period.start and period.end, or null when either
// bound is missing/undateable (an open or period-less encounter has no duration to
// report — we show nothing rather than fabricate a zero). Never negative.
export function visitDurationMin(enc) {
  const start = Date.parse((enc && enc.period && enc.period.start) || '');
  const end = Date.parse((enc && enc.period && enc.period.end) || '');
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  const mins = Math.round((end - start) / 60000);
  return mins >= 0 ? mins : null;
}

// "23 min" / "1 h 5 min" / '' — a compact human duration label (English; callers
// add Hindi if needed). '' for null/0 so the view can omit the cell cleanly.
export function durationLabel(min) {
  const m = Number(min);
  if (!Number.isFinite(m) || m <= 0) return '';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

// shapeHistoryRow(enc, patient, comps) → the live-queue row DTO plus:
//   endIso       — Encounter.period.end (the completed instant), or null
//   durationMin  — whole minutes start→end, or null
//   durationLabel— the compact label ('' when unknown)
// Everything the QueueHistory view renders (token, name, doctor, department,
// state, time) comes straight from shapeQueueRow, so the history list agrees
// field-for-field with the live board.
export function shapeHistoryRow(enc, patient, comps) {
  const base = shapeQueueRow(enc, patient, comps);
  const durationMin = visitDurationMin(enc);
  return {
    ...base,
    endIso: (enc && enc.period && enc.period.end) || null,
    durationMin,
    durationLabel: durationLabel(durationMin),
  };
}
